import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { RefreshIcon, SparkleIcon } from '../components/icons.jsx';

export default function DraftPage() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [story, setStory] = useState(() => location.state?.initial ?? null);
  const [scenes, setScenes] = useState(() => location.state?.initial?.scenes ?? []);
  const [busy, setBusy] = useState(false);
  const [regenIndex, setRegenIndex] = useState(null);
  const [error, setError] = useState(null);

  // Si entramos sin state (refresh), no podemos recargar el draft completo desde el server
  // todavia (no hay endpoint para draft por id sin ser publico). Para el MVP,
  // si se pierde el state mostramos un mensaje.
  useEffect(() => {
    if (!story) setError('No se ha encontrado el boceto. Vuelve a crearlo desde el inicio.');
  }, [story]);

  function updateScene(i, patch) {
    setScenes((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }

  async function saveEdits() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.stories.updateDraft(storyId, { scenes });
      // El backend puede regenerar el scene_prompt de las escenas cuyo texto
      // hayamos editado; sincronizamos el estado local para no perderlo.
      if (res?.scenes) setScenes(res.scenes);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function regenerateScene(i) {
    setRegenIndex(i);
    setError(null);
    try {
      const { scene } = await api.stories.regenerateScene(storyId, i);
      updateScene(i, scene);
    } catch (err) {
      setError(err.message);
    } finally {
      setRegenIndex(null);
    }
  }

  async function generateFinal() {
    setBusy(true);
    setError(null);
    try {
      // updateDraft persiste el texto y, si lo editaste, regenera el prompt de
      // imagen para que las ilustraciones reflejen tus cambios antes de generar.
      const res = await api.stories.updateDraft(storyId, { scenes });
      if (res?.scenes) setScenes(res.scenes);
      const { jobId } = await api.stories.generate(storyId);
      navigate(`/generating/${jobId}`, { state: { slug: story.slug } });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  if (error && !story) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => navigate('/create')}
          className="mt-4 rounded-lg bg-brand-600 px-4 py-2 font-semibold text-white shadow hover:bg-brand-700"
        >
          Crear un cuento nuevo
        </button>
      </div>
    );
  }
  if (!story) return null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="text-3xl font-bold text-brand-700">{story.title}</h1>
      {story.synopsis && <p className="mt-2 text-gray-600">{story.synopsis}</p>}
      <p className="mt-1 text-xs uppercase tracking-wide text-brand-500">Revisa el boceto y editalo a tu gusto</p>
      <p className="mt-1 text-sm text-gray-500">Si cambias el texto de una escena, la ilustracion se adaptara a tu version al generar.</p>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-6 space-y-4">
        {scenes.map((scene, i) => (
          <div key={i} className="rounded-xl border border-brand-100 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
                Escena {i + 1}
              </span>
              <button
                type="button"
                disabled={regenIndex !== null}
                onClick={() => regenerateScene(i)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700 disabled:opacity-50"
              >
                <RefreshIcon size="0.95em" />
                {regenIndex === i ? 'Regenerando...' : 'Regenerar escena'}
              </button>
            </div>
            <textarea
              rows={3}
              className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              value={scene.text}
              onChange={(e) => updateScene(i, { text: e.target.value })}
            />
            <details className="mt-2 text-xs text-gray-500">
              <summary className="cursor-pointer">Prompt de imagen (avanzado)</summary>
              <textarea
                rows={2}
                className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs"
                value={scene.scene_prompt}
                onChange={(e) => updateScene(i, { scene_prompt: e.target.value })}
              />
            </details>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 mt-8 -mx-4 border-t border-brand-100 bg-white/95 px-4 py-4 backdrop-blur">
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            onClick={saveEdits}
            disabled={busy}
            className="rounded-lg border border-brand-200 px-4 py-2 font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Guardar cambios
          </button>
          <button
            onClick={generateFinal}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-6 py-2 font-semibold text-white shadow hover:bg-brand-700 disabled:opacity-50"
          >
            <SparkleIcon size="1.1em" /> Generar ilustraciones y publicar
          </button>
        </div>
      </div>
    </div>
  );
}
